import { noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import styled, { withTheme } from 'styled-components';
import { filterToValidAttributes } from '@zocdoc/dom-property';
import { injectIntl } from 'react-intl';
import { removeWhitespace } from '../../util';

import { breakpoint } from '../../../grid';
import { Link } from '../../../link';
import getLocalizedContent from '../../../utils/getLocalizedContent';

import {
    FacebookLink,
    GooglePlusLink,
    LinkedInLink,
    TwitterLink,
} from '../../Links';

import PaddedContainer from '../../PaddedContainer';
import { Metrics, MetricsClickable } from '@zocdoc/frontend-metrics';

export function shouldRenderLink(uri, location) {
    if (!uri) {
        return false;
    // do not render link to homepage when current route is the homepage
    } else if (location && uri === location.pathname) {
        return false;
    }
    return true;
}

class Legal extends PureComponent {
    static propTypes = {
        location: PropTypes.shape({
            pathname: PropTypes.string,
        }),
        theme: PropTypes.shape({
            footer: PropTypes.shape({
                legal: PropTypes.shape({
                    leftContent: PropTypes.arrayOf(
                        PropTypes.shape({
                            text: PropTypes.string,
                            title: PropTypes.string,
                            uri: PropTypes.string,
                        })
                    ),
                    rightContent: PropTypes.arrayOf(
                        PropTypes.shape({
                            text: PropTypes.string,
                            title: PropTypes.string,
                            uri: PropTypes.string,
                        })
                    ),
                    showZocdocSocialLinks: PropTypes.bool,
                }).isRequired,
            }).isRequired,
        }).isRequired,
        trackLinkClick: PropTypes.func,
    };

    static defaultProps = {
        trackLinkClick: noop,
    };

    renderCustomContent(definition, index) {
        if (shouldRenderLink(definition.uri, this.props.location)) {
            return (
                <MetricsClickable
                    key={index}
                    action={`Click ${definition.text}`}
                    component={definition.componentName ? `${definition.componentName}Links` : 'CustomContent'}
                    element={removeWhitespace(definition.text)}
                >
                    <StyledLink
                        beforeClick={this.handleBeforeCustomLinkClick}
                        data-component-name={definition.componentName}
                        data-test="footer-legal-custom-content-link"
                        href={definition.uri}
                        target="_blank"
                        title={definition.title}
                    >
                        {definition.text}
                    </StyledLink>
                </MetricsClickable>
            );
        } else {
            return (
                <ColoredText
                    key={index}
                    textColor={definition.textColor}
                >
                    {definition.text}
                </ColoredText>
            );
        }
    }


    maybeRenderLeftCustomContent(content) {
        if (content) {
            return (
                <LeftLinkGroup data-test="footer-legal-custom-content-link-group">
                    {content.map(this.renderCustomContent, this)}
                </LeftLinkGroup>
            );
        }
    }

    maybeRenderRightCustomContent(content) {
        if (content) {
            return (
                <RightLinkGroup data-test="footer-legal-custom-content-link-group">
                    {content.map(this.renderCustomContent, this)}
                </RightLinkGroup>
            );
        }
    }

    maybeRenderSocialLinks(shouldRenderSocialLinks) {
        const { linkColor } = this.props.theme.footer.legal;
        if (shouldRenderSocialLinks) {
            return (
                <Metrics component="SocialMediaLinks">
                    <SocialLinkGroup>
                        <MetricsClickable
                            action="Click Facebook"
                            element="Facebook"
                        >
                            <FacebookLink
                                color={linkColor}
                                height={13}
                                onBeforeLinkClick={this.handleBeforeSocialLinkClick}
                            />
                        </MetricsClickable>

                        <MetricsClickable
                            action="Click Twitter"
                            element="Twitter"
                        >
                            <TwitterLink
                                color={linkColor}
                                height={13}
                                onBeforeLinkClick={this.handleBeforeSocialLinkClick}
                            />
                        </MetricsClickable>

                        <MetricsClickable
                            action="Click GooglePlus"
                            element="GooglePlus"
                        >
                            <GooglePlusLink
                                color={linkColor}
                                height={13}
                                onBeforeLinkClick={this.handleBeforeSocialLinkClick}
                            />
                        </MetricsClickable>

                        <MetricsClickable
                            action="Click LinkedIn"
                            element="LinkedIn"
                        >
                            <LinkedInLink
                                color={linkColor}
                                height={13}
                                onBeforeLinkClick={this.handleBeforeSocialLinkClick}
                            />
                        </MetricsClickable>
                    </SocialLinkGroup>
                </Metrics>
            );
        }
    }

    render() {
        const {
            theme: { footer: { legal } },
            theme,
            intl,
            ...props
        } = this.props;

        const leftContent = getLocalizedContent({
            intl,
            theme,
            path: 'footer.legal.leftContent',
        });
        const rightContent = getLocalizedContent({
            intl,
            theme,
            path: 'footer.legal.rightContent',
        });

        return (
            <StyledContainer {...filterToValidAttributes(props)}>
                {this.maybeRenderLeftCustomContent(leftContent)}
                {this.maybeRenderSocialLinks(legal.showZocdocSocialLinks)}
                {this.maybeRenderRightCustomContent(rightContent)}
            </StyledContainer>
        );
    }

    handleBeforeSocialLinkClick = async e => {
        const link = e.currentTarget;
        const elementName = link.getAttribute('data-element-name');

        await this.props.trackLinkClick({
            action: `Click ${elementName}`,
            Component: 'SocialMediaLinks',
            Element: removeWhitespace(elementName),
        });
    };

    handleBeforeCustomLinkClick = async e => {
        const link = e.currentTarget;
        const elementName = link.textContent;
        const componentName = link.getAttribute('data-component-name');

        await this.props.trackLinkClick({
            action: `Click ${elementName}`,
            Component: `${componentName}Links` || 'CustomContent',
            Element: removeWhitespace(elementName),
        });
    }
}

const StyledContainer = styled(PaddedContainer)`
    align-content: flex-start;
    background-color: ${p => p.theme.footer.legal.bgColor};
    color: ${p => p.theme.footer.legal.textColor};
    display: flex;
    flex-grow: 1;
    flex-wrap: wrap;
    line-height: 2;
    padding-top: 14px;
    padding-bottom: 14px;
`;

export const ColoredText = styled.span`
    color: ${p => p.textColor};
`;

export const StyledLink = styled(Link)`
    border-bottom-color: ${p => p.theme.footer.legal.linkUnderlineColor};
    color: ${p => p.theme.footer.legal.linkColor};

    &:focus,
    &:hover {
        border-bottom-color: ${p => p.theme.footer.legal.linkUnderlineColorHover};
        color: ${p => p.theme.footer.legal.linkColorHover};
    }

    &:visited {
        border-bottom-color: ${p => p.theme.footer.legal.linkUnderlineColorVisited};
        color: ${p => p.theme.footer.legal.linkColorVisited};
    }
`;

const LinkGroup = styled.div`
    &:first-child {
        margin-right: auto;
    }
`;

const LeftLinkGroup = styled(LinkGroup)`
    > :not(:last-child) {
        margin-right: 16px;

        ${breakpoint.medium} {
            margin-right: 18px;
        }
    }
`;

const RightLinkGroup = styled(LinkGroup)`
    > :not(:first-child) {
        margin-left: 16px;

        ${breakpoint.medium} {
            margin-left: 18px;
        }
    }
`;

const SocialLinkGroup = styled(RightLinkGroup)`
    color: ${p => p.theme.footer.legal.linkColor};

    > * {
        display: inline-block !important;
        margin-left: 13px !important;
    }

    ${breakpoint('small', { down: true })} {
        display: none;
    }
`;

export default injectIntl(withTheme(Legal));
